/**
 * Service for email change functionality
 * 
 * Handles API calls for:
 * - Requesting email change (sends confirmation to new email)
 * - Confirming email change (validates token)
 * - Getting pending email change request
 * - Cancelling pending email change request
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

interface EmailChangeRequestResponse {
  message: string;
}

interface EmailChangeConfirmResponse {
  newEmail: string;
}

interface PendingEmailChangeResponse {
  newEmail: string;
  expiresAt: string;
}

/**
 * Request an email change. Sends a confirmation email to the new address.
 * 
 * @param token - Auth token
 * @param newEmail - New email address
 * @param currentPassword - Current password for verification
 * @returns API response with success status
 */
export async function requestEmailChange(
  token: string,
  newEmail: string,
  currentPassword: string
): Promise<ApiResponse<EmailChangeRequestResponse>> {
  try {
    console.log('[emailChangeService] requestEmailChange called', { 
      hasToken: !!token, 
      tokenLength: token?.length,
      newEmail,
      url: `${API_BASE_URL}/auth/email/change-request` 
    });
    
    const response = await fetch(`${API_BASE_URL}/auth/email/change-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ newEmail, currentPassword }),
    });

    console.log('[emailChangeService] Response status:', response.status);
    const data = await response.json();
    console.log('[emailChangeService] Response data:', data);
    return data;
  } catch (error) {
    console.error('Error requesting email change:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network error',
      },
    };
  }
}

/**
 * Confirm an email change using the token from the confirmation email.
 * This endpoint is public (no auth required).
 * 
 * @param confirmToken - Token from the confirmation email
 * @returns API response with the new email address
 */
export async function confirmEmailChange(
  confirmToken: string
): Promise<ApiResponse<EmailChangeConfirmResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/email/confirm-change`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: confirmToken }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error confirming email change:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network error',
      },
    };
  }
}

/**
 * Get pending email change request for the current user.
 * 
 * @param token - Auth token
 * @returns API response with pending request details or null
 */
export async function getPendingEmailChange(
  token: string
): Promise<ApiResponse<PendingEmailChangeResponse | null>> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/email/pending`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting pending email change:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network error',
      },
    };
  }
}

/**
 * Cancel a pending email change request.
 * 
 * @param token - Auth token
 * @returns API response with success status
 */
export async function cancelEmailChange(
  token: string
): Promise<ApiResponse<{ message: string }>> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/email/cancel-change`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error cancelling email change:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network error',
      },
    };
  }
}

export const emailChangeService = {
  requestEmailChange,
  confirmEmailChange,
  getPendingEmailChange,
  cancelEmailChange,
};

export default emailChangeService;
